import { DocPage } from '../types/doc';
import ContentBlockRenderer from './ContentBlockRenderer';
import { useDocs } from '../hooks/useDocs';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface Props {
  doc: DocPage;
}

const DocContent = ({ doc }: Props) => {
  const { data: allDocs } = useDocs();
  const { slug } = useParams<{ slug: string }>();

  // Find previous and next docs
  const getNavigation = () => {
    if (!allDocs || !slug) return { prev: null, next: null };

    const publishedDocs = allDocs
      .filter((d) => d.published)
      .sort((a, b) => {
        if (a.category !== b.category) return a.category.localeCompare(b.category);
        if (a.order !== b.order) return a.order - b.order;
        return a.title.localeCompare(b.title);
      });

    const currentIndex = publishedDocs.findIndex((d) => d.slug === slug);

    return {
      prev: currentIndex > 0 ? publishedDocs[currentIndex - 1] : null,
      next: currentIndex < publishedDocs.length - 1 ? publishedDocs[currentIndex + 1] : null
    };
  };

  const { prev, next } = getNavigation();
  return (
    <article className="prose prose-slate max-w-none dark:prose-invert">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-card backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-500">
          {doc.category}
        </p>
        <h1 className="mt-2 text-4xl font-bold">{doc.title}</h1>
        {doc.description ? <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">{doc.description}</p> : null}
        {doc.heroMedia?.url ? (
          <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
            {doc.heroMedia.type === 'image' ? (
              <img src={doc.heroMedia.url} alt={doc.heroMedia.caption || doc.title} className="w-full object-cover" />
            ) : (
              <div className="aspect-video">
                <iframe
                  title={doc.title}
                  src={doc.heroMedia.url}
                  className="h-full w-full border-0"
                  allowFullScreen
                />
              </div>
            )}
            {doc.heroMedia.caption ? (
              <p className="bg-slate-50 px-4 py-2 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                {doc.heroMedia.caption}
              </p>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="mt-8 space-y-12">
        {doc.sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <section key={section.slug} id={section.slug} className="scroll-mt-24">
              <h2 className="text-3xl font-semibold text-slate-900 dark:text-white">{section.title}</h2>
              <div className="mt-4 space-y-6">
                {section.blocks.map((block, idx) => (
                  <ContentBlockRenderer key={`${section.slug}-${idx}`} block={block} />
                ))}
              </div>
            </section>
          ))}
      </div>

      {(prev || next) && (
        <nav className="mt-12 flex flex-col gap-4 border-t border-slate-200 pt-8 dark:border-slate-800 sm:flex-row sm:justify-between">
          {prev ? (
            <Link
              to={`/docs/${prev.slug}`}
              className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 transition hover:border-brand-300 hover:bg-brand-50 dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-brand-700 dark:hover:bg-slate-800"
            >
              <ChevronLeftIcon className="h-5 w-5 text-slate-400 transition group-hover:text-brand-600 dark:group-hover:text-brand-400" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Previous
                </p>
                <p className="mt-1 font-semibold text-slate-900 dark:text-white">{prev.title}</p>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {next ? (
            <Link
              to={`/docs/${next.slug}`}
              className="group ml-auto flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 text-right transition hover:border-brand-300 hover:bg-brand-50 dark:border-slate-800 dark:bg-slate-900/60 dark:hover:border-brand-700 dark:hover:bg-slate-800"
            >
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Next
                </p>
                <p className="mt-1 font-semibold text-slate-900 dark:text-white">{next.title}</p>
              </div>
              <ChevronRightIcon className="h-5 w-5 text-slate-400 transition group-hover:text-brand-600 dark:group-hover:text-brand-400" />
            </Link>
          ) : null}
        </nav>
      )}
    </article>
  );
};

export default DocContent;

